import React from 'react';
import * as R from 'ramda';
import {doesNotEqual, exists} from 'js/common/object_helper';
import {toTitleCase} from 'js/common/string_helper';
import {getCountryLocationArray} from 'js/common/country_helper';

const {campaign} = window.sn;
const {
    CampaignAudienceAge,
    CampaignAudienceGender,
    CampaignAudienceLocation,
    CountryCodes
} = window.sn.enums;

export function makeReactivatedFlag(approvedPostsCount) {
    let reactivatedMessage;
    let reactivatedClass;

    if (approvedPostsCount) {
        reactivatedMessage = 'Reactivated';
        reactivatedClass = 'text-success';
    } else {
        reactivatedMessage = 'Not Reactivated';
        reactivatedClass = 'text-danger';
    }

    return <p className={reactivatedClass}>{reactivatedMessage}</p>;
}

/**
 * @export
 * @param {number} age
 * @return {*}
 */
export function isWrongAge(age) {
    const {
        AGE_13_TO_17,
        AGE_18_TO_24,
        AGE_25_TO_34,
        AGE_35_TO_44,
        AGE_45_TO_54,
        AGE_55_PLUS
    } = CampaignAudienceAge;

    if (R.isNil(age)) {
        return null;
    }

    const audienceAges = R.pipe(R.prop('audience_age'), R.split(','))(campaign);

    if (R.contains(CampaignAudienceAge.ALL, audienceAges)) {
        return false;
    }

    if (age >= 13 && age <= 17) {
        if (R.contains(AGE_13_TO_17, audienceAges)) {
            return false;
        }
    }

    if (age >= 18 && age <= 24) {
        if (R.contains(AGE_18_TO_24, audienceAges)) {
            return false;
        }
    }

    if (age >= 25 && age <= 34) {
        if (R.contains(AGE_25_TO_34, audienceAges)) {
            return false;
        }
    }

    if (age >= 35 && age <= 44) {
        if (R.contains(AGE_35_TO_44, audienceAges)) {
            return false;
        }
    }

    if (age >= 45 && age <= 54) {
        if (R.contains(AGE_45_TO_54, audienceAges)) {
            return false;
        }
    }

    if (age >= 55) {
        if (R.contains(AGE_55_PLUS, audienceAges)) {
            return false;
        }
    }

    return true;
}

export function makeAgeFlag(age, messageOnly = false) {
    const {age_21_plus: campaign21Plus, audience_age: campaignAudienceAge} = campaign;

    let ageMessage = 'No age';
    let ageClass = 'text-warning';
    let under21 = false;
    if (campaign21Plus && age < 21) under21 = true;

    const wrongAge = isWrongAge(age);

    if (wrongAge) {
        ageMessage = `Wrong age: ${age} (not in ${campaignAudienceAge})`;
        ageClass = 'text-danger';
    } else if (under21) {
        ageMessage = `Wrong age: ${age} (under 21)`;
        ageClass = 'text-danger';
    } else if (age) {
        ageMessage = `Age: ${age}`;
        ageClass = 'text-success';
    }

    if (messageOnly) return ageMessage;

    return <p className={`invite-flag ${ageClass}`}>{ageMessage}</p>;
}

export function isWrongGender(gender) {
    if (R.isNil(gender)) return null;
    const {audience_gender} = campaign;

    const campaignHasGenderPreference =
        exists(audience_gender) && doesNotEqual(audience_gender, CampaignAudienceGender.ALL);

    if (!campaignHasGenderPreference) {
        return false;
    }

    if (campaignHasGenderPreference) {
        return R.equals(gender, audience_gender);
    }

    return true;
}

/**
 * @todo use isWrongGender
 * @param string|null gender
 */
export function makeGenderFlag(gender, messageOnly = false) {
    const campaignGenderPreference = campaign.audience_gender;
    const campaignHasGenderPreference =
        !(campaignGenderPreference === 'all') && !(campaignGenderPreference === undefined);
    let genderMessage = 'No gender';
    let genderClass = 'text-warning';
    if (gender && campaignHasGenderPreference && gender !== campaignGenderPreference) {
        genderMessage = `Wrong gender: ${gender} (not ${campaignGenderPreference})`;
        genderClass = 'text-danger';
    } else if (gender) {
        genderMessage = `Gender: ${gender}`;
        genderClass = 'text-success';
    }

    if (messageOnly) return genderMessage;

    return <p className={`invite-flag ${genderClass}`}>{genderMessage}</p>;
}

export function isWrongCountry(country) {
    if (R.isNil(country)) return null;

    const {audience_location} = campaign;

    const campaignHasCountryPreference =
        exists(audience_location) &&
        doesNotEqual(audience_location, CampaignAudienceLocation.WORLDWIDE);

    if (campaignHasCountryPreference) {
        const countryLocations = getCountryLocationArray(country);

        if (R.contains(audience_location, countryLocations)) {
            return false;
        }

        return true;
    }

    return true;
}

/**
 * @todo us isWrongCountry
 * @param string|null country
 */
export function makeCountryFlag(country, messageOnly = false) {
    const {WORLDWIDE} = CampaignAudienceLocation;
    const campaignLocation = campaign.audience_location;
    const campaignHasCountry =
        !(campaignLocation === WORLDWIDE) && !(campaignLocation === undefined);
    const countryLocations = getCountryLocationArray(country);
    let countryMessage = 'No country';
    let countryClass = 'text-warning';
    if (country && campaignHasCountry && !R.contains(campaignLocation, countryLocations)) {
        countryMessage = `Wrong country: ${CountryCodes[country] || country} (not in '${toTitleCase(
            campaignLocation
        )}')`;
        countryClass = 'text-danger';
    } else if (country) {
        countryMessage = `Country: ${CountryCodes[country] || country}`;
        countryClass = 'text-success';
    }

    if (messageOnly) return countryMessage;

    return <p className={`invite-flag ${countryClass}`}>{countryMessage}</p>;
}

export function makeProfileFlag(profileId) {
    const profileMessage = profileId ? 'Has profile' : 'No profile';
    const profileClass = profileId ? 'text-success' : 'text-danger';
    return <p className={`invite-flag ${profileClass}`}>{profileMessage}</p>;
}



// WEBPACK FOOTER //
// ./common/flag_helper.js